import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { Link } from "wouter";
import {
  CalendarDays,
  Scissors,
  Star,
  MessageCircle,
  BarChart3,
  Smartphone,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Users,
  Clock,
  Shield,
  ChevronDown,
  ChevronUp,
  Zap,
  Globe,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";

// ─── FAQ Item ────────────────────────────────────────────────────────────────
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-white/10 rounded-xl overflow-hidden cursor-pointer"
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors">
        <span className="font-medium text-white">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-primary flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </div>
      {open && (
        <div className="px-6 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-white/10 pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function Home() {
  const { isAuthenticated, loading, user } = useAuth();
  const [, navigate] = useLocation();
  const { data: plans } = trpc.plans.list.useQuery();
  const isSuperAdmin = user?.role === "superadmin";
  const dashboardPath = isSuperAdmin ? "/superadmin" : "/admin";

  // Redirecionar automaticamente usuários autenticados para a Agenda Semanal
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(isSuperAdmin ? "/superadmin" : "/admin/weekly-calendar");
    }
  }, [isAuthenticated, loading, isSuperAdmin]);

  const features = [
    {
      icon: CalendarDays,
      title: "Agendamento Online 24/7",
      desc: "Seus clientes agendam pelo site ou WhatsApp a qualquer hora, sem precisar ligar. Você recebe a confirmação automática.",
      color: "text-violet-400",
      bg: "bg-violet-500/10",
    },
    {
      icon: MessageCircle,
      title: "Assistente de IA no WhatsApp",
      desc: "Um agente inteligente responde dúvidas, apresenta serviços e agenda horários diretamente no WhatsApp do seu salão.",
      color: "text-emerald-400",
      bg: "bg-emerald-500/10",
    },
    {
      icon: BarChart3,
      title: "Painel Completo de Gestão",
      desc: "Visualize agendamentos, faturamento, profissionais e avaliações em um único painel. Tome decisões com dados reais.",
      color: "text-blue-400",
      bg: "bg-blue-500/10",
    },
    {
      icon: Smartphone,
      title: "Funciona no Celular",
      desc: "Acesse agenda, caixa, fila e atalhos do turno em uma interface adaptada para celular.",
      color: "text-amber-400",
      bg: "bg-amber-500/10",
    },
    {
      icon: Users,
      title: "Relacionamento com clientes",
      desc: "Centralize histórico, pacotes, créditos e preferências para acompanhar cada cliente com contexto.",
      color: "text-pink-400",
      bg: "bg-pink-500/10",
    },
    {
      icon: Shield,
      title: "Seguro e Confiável",
      desc: "Acesse dados do seu salão com perfis de permissão e acompanhe a operação em um ambiente organizado.",
      color: "text-cyan-400",
      bg: "bg-cyan-500/10",
    },
  ];

  const faqs = [
    {
      q: "Preciso de conhecimento técnico para usar?",
      a: "Não. O onboarding orienta o cadastro do salão, serviços, profissionais e horários em etapas claras.",
    },
    {
      q: "Como funciona o assistente de IA no WhatsApp?",
      a: "Após o cadastro, nossa equipe configura um assistente personalizado com o nome, a personalidade e os serviços do seu salão. Ele responde automaticamente no WhatsApp do seu número.",
    },
    {
      q: "Posso cancelar a qualquer momento?",
      a: "As condições de assinatura e cancelamento são apresentadas no momento da contratação e ficam disponíveis no portal de cobrança.",
    },
    {
      q: "O sistema funciona para barbearias também?",
      a: "Sim! O sistema atende salões de beleza, barbearias, clínicas de estética, nail designers e qualquer negócio que trabalhe com agendamentos.",
    },
    {
      q: "Meus clientes precisam criar conta para agendar?",
      a: "Não necessariamente. O agendamento pode ser feito pelo assistente de IA no WhatsApp sem nenhum cadastro. Para agendar pelo site, o login é simples e rápido.",
    },
  ];

  // Splash screen enquanto a autenticação é verificada
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img
            src="https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/beleza-bronze-logo-dark_41784aae.png"
            alt="Beleza & Bronze"
            className="h-10 w-auto object-contain animate-pulse"
          />
          <div className="flex gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-[Inter,sans-serif]">

      {/* ── Navigation ────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/8 bg-[#0a0a0a]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <img
              src="https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/beleza-bronze-logo-dark_41784aae.png"
              alt="Beleza & Bronze"
              className="h-8 w-auto object-contain"
            />
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/60">
            <a href="#features" className="hover:text-white transition-colors">Funcionalidades</a>
            <a href="#pricing" className="hover:text-white transition-colors">Planos</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            <Link href="/sobre" className="hover:text-white transition-colors">Sobre</Link>
            <Link href="/booking" className="hover:text-white transition-colors">Demo</Link>
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <Link href={dashboardPath}>
                <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Meu Painel <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            ) : (
              <>
                <a href="/login" className="text-sm text-white/60 hover:text-white transition-colors hidden md:block">
                  Entrar
                </a>
                <Link href="/onboarding">
                  <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                    Começar grátis
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="pt-32 pb-24 px-4 text-center relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/20 rounded-full blur-[120px] opacity-60" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10">
          <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 hover:bg-primary/20 px-4 py-1.5 text-xs font-medium">
            <Sparkles className="w-3 h-3 mr-1.5" />
            Software de gestão para salões de beleza e clínicas de bronzeamento
          </Badge>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-6">
            Seu salão ou clínica de bronzeamento{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-400">
              organizado, do seu jeito
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed">
            Agendamento online, assistente de IA no WhatsApp e painel de gestão completo.
            Recursos para organizar atendimento, equipe e rotina em uma única plataforma.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link href="/onboarding">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 text-base font-medium shadow-lg shadow-primary/25">
                Começar grátis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link href="/booking">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 h-12 text-base">
                Ver demonstração
              </Button>
            </Link>
          </div>

          <p className="text-xs text-white/30 mt-4">
            Sem cartão de crédito · Comece pelo cadastro da sua operação · Consulte as condições de assinatura
          </p>
        </div>

        {/* Dashboard mockup */}
        <div className="max-w-5xl mx-auto mt-16 relative z-10">
          <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden shadow-2xl shadow-black/50">
            <div className="bg-white/5 border-b border-white/10 px-4 py-3 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/60" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
              <div className="w-3 h-3 rounded-full bg-green-500/60" />
              <span className="text-xs text-white/30 ml-2">Visão ilustrativa do painel</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 md:p-6">
              {[
                { label: "Agenda", value: "Organize horários", icon: CalendarDays, color: "text-violet-400" },
                { label: "Financeiro", value: "Acompanhe recebimentos", icon: BarChart3, color: "text-emerald-400" },
                { label: "Clientes", value: "Centralize cadastros", icon: Users, color: "text-blue-400" },
              ].map((stat) => (
                <div key={stat.label} className="bg-white/5 rounded-xl p-4 border border-white/8">
                  <stat.icon className={`w-5 h-5 ${stat.color} mb-2`} />
                  <div className="text-base font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-white/40 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
            <div className="px-6 pb-6">
              <div className="bg-white/5 rounded-xl border border-white/8 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-white/80">Exemplo de agenda</span>
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Ilustrativo</Badge>
                </div>
                {[
                  { time: "09:00", client: "Horário reservado", service: "Serviço agendado", pro: "Equipe" },
                  { time: "10:30", client: "Horário reservado", service: "Serviço agendado", pro: "Equipe" },
                  { time: "11:00", client: "Horário reservado", service: "Serviço agendado", pro: "Equipe" },
                ].map((appt) => (
                  <div key={appt.time} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                    <span className="text-xs text-primary font-mono w-10">{appt.time}</span>
                    <span className="text-sm text-white/80 flex-1">{appt.client}</span>
                    <span className="text-xs text-white/40 hidden sm:block">{appt.service}</span>
                    <Badge className="bg-white/5 text-white/50 border-white/10 text-xs">{appt.pro}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Recursos-chave ─────────────────────────────────────────── */}
      <section className="border-y border-white/8 py-6 bg-white/2">
        <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center gap-x-12 gap-y-4 text-center">
          {[
            { value: "Agenda", label: "organizada por profissional" },
            { value: "WhatsApp", label: "atendimento com IA" },
            { value: "Caixa", label: "recebimentos e comissões" },
            { value: "Gestão", label: "dados por salão" },
          ].map((s) => (
            <div key={s.label}>
              <div className="text-2xl font-bold text-white">{s.value}</div>
              <div className="text-xs text-white/40 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────── */}
      <section id="features" className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-white/5 text-white/60 border-white/10">Funcionalidades</Badge>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              Tudo que seu salão precisa
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Uma plataforma completa para automatizar o dia a dia e focar no que realmente importa: atender bem.
            </p>
          </div>

          {/* Cards visuais com imagens */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            {[
              { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/bb-post-02-agendamento_5accca7f.png", alt: "Agendamento online 24h" },
              { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/bb-post-03-ia_29e84d46.png", alt: "IA no WhatsApp" },
              { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/bb-post-04-caixa_4e5f0f77.png", alt: "Controle de caixa" },
              { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/promo_15dias_167e6d66.png", alt: "Planos e preços" },
              { src: "https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/bb-post-01-lancamento_5dc78427.png", alt: "Sistema completo" },
            ].map((img) => (
              <div key={img.alt} className="rounded-2xl overflow-hidden border border-white/10 hover:border-primary/40 transition-all hover:scale-[1.02] cursor-pointer group">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {/* Cards de features com ícones */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-white/8 bg-white/3 p-6 hover:bg-white/6 hover:border-white/15 transition-all group"
              >
                <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center mb-4`}>
                  <f.icon className={`w-5 h-5 ${f.color}`} />
                </div>
                <h3 className="font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────── */}
      <section className="py-24 px-4 bg-white/2 border-y border-white/8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-white/5 text-white/60 border-white/10">Como funciona</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Pronto em 3 passos simples
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", icon: Zap, title: "Cadastre seu salão", desc: "Crie sua conta, adicione seus serviços, profissionais e horários de funcionamento em minutos." },
              { step: "02", icon: Globe, title: "Ative o assistente", desc: "Nossa equipe configura o assistente de IA personalizado para o seu salão e conecta ao seu WhatsApp." },
              { step: "03", icon: CalendarDays, title: "Receba agendamentos", desc: "Seus clientes agendam 24/7 pelo WhatsApp ou pelo site. Você gerencia tudo no painel." },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="text-xs text-primary font-mono mb-2">{item.step}</div>
                <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────────── */}
      <section id="pricing" className="py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-white/5 text-white/60 border-white/10">Planos</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Preço justo, sem surpresas
            </h2>
            <p className="text-white/50">Comece grátis. Escale quando precisar.</p>
          </div>

          {plans && plans.length > 0 ? (
            <div className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-${Math.min(plans.length, 4)} gap-6`}>
              {plans.map((plan, idx: number) => {
                const isPopular = plan.slug === 'professional';
                const isElite = plan.slug === 'elite';
                // Montar lista de features a partir dos campos booleanos do plano
                const planFeatures: string[] = [
                  `Até ${plan.maxProfessionals === 999 ? "ilimitados" : plan.maxProfessionals} profissional${plan.maxProfessionals === 1 ? "" : "is"}`,
                  "Agendamento online 24/7",
                  "Painel de gestão completo",
                  ...(plan.hasAiChatbot ? ["Assistente de IA no WhatsApp"] : []),
                  ...(plan.hasOnlinePayment ? ["Pagamento online"] : []),
                  ...(plan.hasAdvancedReports ? ["Relatórios avançados"] : []),
                  ...(plan.hasVoiceNotes ? ["Notas de voz"] : []),
                  ...((plan as any).hasPanelChamada ? ["Painel de chamada de clientes (TV)"] : []),
                  ...((plan as any).hasMediaPromocional ? ["Mídia promocional no painel TV"] : []),
                  ...((plan as any).hasAiWhatsapp ? ["IA integrada no WhatsApp"] : []),
                  ...((plan as any).hasAiInstagram ? ["IA integrada no Instagram"] : []),
                  ...((plan as any).hasAiInstagram || (plan as any).hasPanelChamada ? ["Sistema de Filiais (múltiplas unidades)"] : []),
                ];
                return (
                  <div
                    key={plan.id}
                    className={`rounded-2xl border p-8 relative flex flex-col ${
                      isElite
                        ? "border-yellow-500/60 bg-gradient-to-b from-yellow-500/10 to-amber-900/10 shadow-lg shadow-yellow-500/20"
                        : isPopular
                        ? "border-primary bg-primary/10 shadow-lg shadow-primary/20"
                        : "border-white/10 bg-white/3"
                    }`}
                  >
                    {isElite && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-yellow-500 text-black border-0 px-3 text-xs font-bold">Mais completo</Badge>
                      </div>
                    )}
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-white mb-1">{plan.name}</h3>
                      <p className="text-sm text-white/50 mb-4">{plan.description ?? "Ideal para seu negócio"}</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-white">
                          R$ {Number(plan.priceMonthly).toFixed(0)}
                        </span>
                        <span className="text-white/40 text-sm">/mês</span>
                      </div>
                      <p className="text-xs text-emerald-400 mt-1">Teste grátis por 15 dias</p>
                    </div>

                    <ul className="space-y-3 flex-1 mb-8">
                      {planFeatures.length > 0 ? planFeatures.map((feat: string) => (
                        <li key={feat} className="flex items-start gap-2 text-sm text-white/70">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                          {feat}
                        </li>
                      )) : (
                        <>
                          <li className="flex items-center gap-2 text-sm text-white/70"><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />Agendamento online</li>
                          <li className="flex items-center gap-2 text-sm text-white/70"><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />Painel de gestão</li>
                          <li className="flex items-center gap-2 text-sm text-white/70"><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />Suporte por WhatsApp</li>
                        </>
                      )}
                    </ul>

                    <Link href="/onboarding">
                      <Button
                        className={`w-full ${isElite ? "bg-yellow-500 hover:bg-yellow-400 text-black font-bold" : isPopular ? "bg-primary hover:bg-primary/90 text-primary-foreground" : "bg-white/10 hover:bg-white/15 text-white border-0"}`}
                      >
                        Começar agora
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/3 p-8 text-center">
              <p className="text-white/60">Os planos não estão disponíveis para consulta neste momento.</p>
              <Link href="/onboarding"><Button className="mt-5 bg-primary hover:bg-primary/90 text-primary-foreground">Iniciar cadastro <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            </div>
          )}
        </div>
      </section>

      {/* ── Implantação assistida ─────────────────────────────────── */}
      <section className="py-24 px-4 bg-white/2 border-y border-white/8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-white/5 text-white/60 border-white/10">Implantação</Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Estruture a operação desde o primeiro acesso
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Cadastre sua estrutura", description: "Organize serviços, profissionais, horários e regras de atendimento conforme a rotina do salão." },
              { title: "Conecte seus canais", description: "Acompanhe a configuração do atendimento, da agenda pública e dos canais que sua operação utiliza." },
              { title: "Acompanhe a evolução", description: "Use os painéis de agenda, caixa, clientes e relatórios para conduzir decisões diárias." },
            ].map((step, index) => (
              <div key={step.title} className="rounded-2xl border border-white/10 bg-white/3 p-6">
                <div className="mb-4 flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">{index + 1}</div>
                <div className="font-medium text-white text-sm mb-2">{step.title}</div>
                <p className="text-sm text-white/60 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────── */}
      <section id="faq" className="py-24 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-white/5 text-white/60 border-white/10">FAQ</Badge>
            <h2 className="text-3xl font-bold tracking-tight">Perguntas frequentes</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Final ─────────────────────────────────────────────── */}
      <section className="py-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
            <div className="rounded-3xl border border-primary/30 bg-primary/10 p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <Sparkles className="w-8 h-8 text-primary mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Pronto para transformar seu salão?
              </h2>
              <p className="text-white/60 mb-8">
                Conheça uma forma mais organizada de conduzir agendamentos, atendimento e gestão da rotina.
              </p>
              <Link href="/onboarding">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-10 h-12 text-base font-medium shadow-lg shadow-primary/30">
                  Começar grátis agora
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <p className="text-xs text-white/30 mt-4">
                Sem cartão de crédito · Cancele quando quiser
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer className="border-t border-white/8 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img
                src="https://d2xsxph8kpxj0f.cloudfront.net/310519663060488325/HwPFMdBZkU5a5SNvfRYMgW/beleza-bronze-logo-dark_41784aae.png"
                alt="Beleza & Bronze"
                className="h-7 w-auto object-contain"
              />
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-white/40">
              <a href="#features" className="hover:text-white transition-colors">Funcionalidades</a>
              <a href="#pricing" className="hover:text-white transition-colors">Planos</a>
              <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
              <Link href="/booking" className="hover:text-white transition-colors">Demo</Link>
              <Link href="/sobre" className="hover:text-white transition-colors">Sobre</Link>
              <Link href="/admin" className="hover:text-white transition-colors">Painel</Link>
            </div>
            <p className="text-xs text-white/20">© 2025 Beleza & Bronze. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
